"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Send, Loader2, CheckCircle, AlertCircle, Phone, MessageCircle } from "lucide-react"
import Link from "next/link"

const propertyTypes = [
  { value: "apartment", label: "شقة سكنية" },
  { value: "villa", label: "فيلا" },
  { value: "duplex", label: "دوبلكس" },
  { value: "studio", label: "استوديو" },
  { value: "commercial", label: "وحدة تجارية" },
  { value: "other", label: "أخرى" },
]

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    propertyType: "",
    message: "",
  })
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus("loading")

    const whatsappMessage = `مرحباً، أنا ${formData.name}%0Aأرغب في الاستفسار عن: ${formData.propertyType ? propertyTypes.find(t => t.value === formData.propertyType)?.label : 'عقار'}%0A${formData.message ? `ملاحظات: ${formData.message}` : ''}%0Aرقمي: ${formData.phone}`
    
    window.open(`https://wa.me/201212129070?text=${whatsappMessage}`, '_blank')
    setStatus("success")
    setFormData({ name: "", phone: "", propertyType: "", message: "" })
    setTimeout(() => setStatus("idle"), 5000)
  }

  return (
    <section id="contact" className="px-6 py-24">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-medium text-brand uppercase tracking-wider mb-4">تواصل معنا</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-zinc-100 mb-4">
            ابدأ رحلتك نحو منزل أحلامك
          </h2>
          <p className="text-zinc-500 max-w-xl mx-auto text-balance">
            أرسل لنا استفسارك وسنتواصل معك في أقرب وقت لمساعدتك في اختيار العقار المناسب
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Link
            href="tel:+201212129070"
            className="flex items-center gap-4 p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-brand/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-brand/30 flex items-center justify-center group-hover:bg-brand/50 transition-colors">
              <Phone className="w-6 h-6 text-brand" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">اتصل بنا</p>
              <p className="text-zinc-100 font-medium" dir="ltr">+20 12 12129070</p>
            </div>
          </Link>

          <Link
            href="https://wa.me/201212129070"
            target="_blank"
            className="flex items-center gap-4 p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-green-700/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-green-900/30 flex items-center justify-center group-hover:bg-green-900/50 transition-colors">
              <MessageCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">واتساب</p>
              <p className="text-green-400 font-medium" dir="ltr">+20 12 12129070</p>
            </div>
          </Link>

          <Link
            href="tel:+201212129070"
            className="flex items-center gap-4 p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-brand/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-900/50 transition-colors">
              <Send className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">استشارة مجانية</p>
              <p className="text-zinc-100 font-medium">تواصل الآن</p>
            </div>
          </Link>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-8"
        >
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-zinc-300 mb-2">
                  الاسم الكامل
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand/50 transition-colors"
                  placeholder="أدخل اسمك الكامل"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-zinc-300 mb-2">
                  رقم الموبايل
                </label>
                <input
                  type="tel"
                  id="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand/50 transition-colors"
                  placeholder="أدخل رقم الموبايل"
                  dir="ltr"
                />
              </div>
            </div>

            <div>
              <label htmlFor="propertyType" className="block text-sm font-medium text-zinc-300 mb-2">
                نوع العقار المطلوب
              </label>
              <select
                id="propertyType"
                value={formData.propertyType}
                onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-zinc-100 focus:outline-none focus:border-brand/50 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="text-zinc-500">
                  اختر نوع العقار
                </option>
                {propertyTypes.map((type) => (
                  <option key={type.value} value={type.value} className="bg-zinc-800">
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-zinc-300 mb-2">
                ملاحظات إضافية (اختياري)
              </label>
              <textarea
                id="message"
                rows={3}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand/50 transition-colors resize-none"
                placeholder="أخبرنا بأي تفاصيل إضافية..."
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-brand hover:bg-brand disabled:bg-brand-dark text-white font-medium rounded-xl transition-colors"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جاري الإرسال...</span>
                </>
              ) : (
                <>
                  <MessageCircle className="w-5 h-5" />
                  <span>إرسال عبر واتساب</span>
                </>
              )}
            </button>

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-4 bg-green-900/30 border border-green-700/50 rounded-xl text-green-400"
              >
                <CheckCircle className="w-5 h-5" />
                <span>تم فتح واتساب! سنتواصل معك قريباً</span>
              </motion.div>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  )
}
